import { History } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, timeAgo } from "../lib/format";
import {
  notificationTypeLabel,
  resolveNotificationPath,
  type NotificationItem,
} from "../lib/notifications";
import { useShellTabs } from "../stores/shell-tabs";
import { Button, PageLoader } from "../components/ui";

export function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "all"],
    queryFn: () => api<{ items: NotificationItem[]; unread: number }>("/notifications"),
    staleTime: 15_000,
  });

  const markReadMut = useMutation({
    mutationFn: (ids?: string[]) => api("/notifications/read", { method: "POST", body: ids ? { ids } : {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  function openNotification(item: NotificationItem) {
    if (!item.readAt) {
      markReadMut.mutate([item.id]);
    }
    const path = resolveNotificationPath(item);
    if (path) {
      navigate(openInActiveTab(path));
    }
  }

  if (isLoading) return <PageLoader />;

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-lg font-semibold text-white/90">Notifications</h1>
          <p className="text-xs text-white/45">
            {unread > 0 ? `${unread} unread` : "You're all caught up"}
          </p>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={() => markReadMut.mutate(undefined)}>
            Mark all read
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        {items.length ? (
          items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`flex w-full gap-3 border-b border-line/60 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-white/[0.03] ${
                item.readAt ? "opacity-75" : "bg-brand-pink/[0.05]"
              }`}
              onClick={() => openNotification(item)}
            >
              <History size={16} className="mt-0.5 shrink-0 text-white/35" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white/90">{item.title}</p>
                {(item.body || item.type) && (
                  <p className="mt-0.5 text-xs text-white/55">
                    {item.body ?? notificationTypeLabel(item.type)}
                  </p>
                )}
                <p className="mt-1 text-[10px] text-white/35" title={fmtDate(item.createdAt)}>
                  {timeAgo(item.createdAt)}
                </p>
              </div>
            </button>
          ))
        ) : (
          <p className="px-4 py-16 text-center text-sm text-white/40">No notifications yet</p>
        )}
      </div>
    </div>
  );
}
